import CustomerContactSelect from "components/Customer/CustomerContactSelect";
import EmailBodyEditor from "components/EmailBodyEditor";
import Spinner from "components/Spinner";
import { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import authHeader from "services/auth-header";
import API from "services/axios";
import emailBodyHtml from "utils/emailBodyHtml";
import {
  fillMultipleShipmentTemplate,
  fillShipmentTemplate,
} from "./shipmentEmailTemplate";

// Same label column as Send Approval, so the two modals read alike.
const LABEL_COL_WIDTH = 75;

/**
 * Email one shipment to the customer (user, 2026-10-26).
 *
 * Opens with the location's Shipment template (Settings -> Locations, filled
 * by fillShipmentTemplate); with no template, Subject and Body are empty
 * (user, 2026-10-26: "if there is no template then show nothing"). To is
 * the linked work order's contact email, and nothing else (empty when no
 * work order is linked). Everything is editable. It is sent from the user's own SMTP for
 * the row's location -- shown as "Email from", as on Send Approval -- and a
 * row with no location asks which one to send from. On success the API marks
 * the tracking number emailed (it leaves the "Not Emailed" tab).
 *
 * Bulk Email (user, 2026-10-27): `items` -- several rows in ONE email, with
 * the location's Multiple Shipments template (type "shipment-multiple");
 * << Shipments >> is each row's clipboard slip (`slips`). With no template
 * the body is just the slips.
 *
 * item       the Shipments row (null = closed)
 * items      instead of item: the selected rows (Bulk Email)
 * slips      the rows' clipboard texts, for Bulk Email
 * trackingUrl, shipDate   for the single template
 * locations  the company's locations (for a row with none)
 * onSent(stamp), onHide
 */
export default function ShipmentEmailModal({
  item: singleItem,
  items: bulkItems,
  slips = [],
  trackingUrl,
  shipDate,
  locations = [],
  onSent,
  onHide,
}) {
  const isBulk = Array.isArray(bulkItems) && bulkItems.length > 0;
  // The row that gives the location and the To: the first with a location.
  const item = isBulk
    ? bulkItems.find((r) => r.locationId) || bulkItems[0]
    : singleItem;
  const [emailTo, setEmailTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // What the editor shows; `key` reloads it (a template filled in), typing
  // only changes `body`.
  const [bodyLoad, setBodyLoad] = useState({ html: "", key: 0 });
  const loadBody = (html) => {
    setBody(html);
    setBodyLoad((b) => ({ html, key: b.key + 1 }));
  };
  const [locationId, setLocationId] = useState("");
  const [smtpEmail, setSmtpEmail] = useState(null);
  const [smtpChecked, setSmtpChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showContacts, setShowContacts] = useState(false);
  // The linked customer whose contacts the button lists (user, 2026-10-29);
  // Bulk Email: the first ticked row that has one. Per-company number.
  const contactCustomerId = isBulk
    ? bulkItems.find((r) => r.customerId)?.customerId
    : item?.customerId;

  // Picked contacts go on the end of To, comma separated, once each.
  const addContacts = (emails = []) => {
    setEmailTo((prev) => {
      const list = prev.split(",").map((e) => e.trim()).filter(Boolean);
      const seen = new Set(list.map((e) => e.toLowerCase()));
      emails
        .map((e) => String(e || "").trim())
        .filter((e) => e && !seen.has(e.toLowerCase()) && seen.add(e.toLowerCase()))
        .forEach((e) => list.push(e));
      return list.join(", ");
    });
    setShowContacts(false);
  };

  // Fresh every time a row is opened. To: the linked work order's contact
  // email ONLY (the list sends it as woContactEmail) -- no fallback to the
  // shipment's own email (never filled) or the customer's default contact
  // (user, 2026-10-26: "only use WO email"). Typed by hand otherwise.
  useEffect(() => {
    if (!item) return;
    setEmailTo(
      isBulk
        ? [...new Set(bulkItems.map((r) => r.woContactEmail).filter(Boolean))].join(
            ", ",
          )
        : item.woContactEmail || "",
    );
    setLocationId(item.locationId ? String(item.locationId) : "");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Subject and Body: the location's Shipment template (Settings ->
  // Locations), filled from the row -- or empty when the location has none.
  // Asked again when the location is chosen for a row that had none.
  useEffect(() => {
    setSubject("");
    loadBody("");
    if (!item) return undefined;
    // Bulk Email with no template (or no location yet) is still the list.
    if (isBulk) loadBody(fillMultipleShipmentTemplate("", bulkItems, { slips }));
    if (!locationId) return undefined;
    let live = true;
    const locationName =
      locations.find((l) => String(l.id) === String(locationId))?.locationName ??
      item.location?.locationName;
    const type = isBulk ? "shipment-multiple" : "shipment";
    API.get(
      `/locations/${encodeURIComponent(locationId)}/templates/${type}`,
      { headers: authHeader() },
    )
      .then(({ data }) => {
        const t = data?.data;
        if (!live || !t) return;
        if (isBulk) {
          const ctx = { slips, locationName };
          setSubject(fillMultipleShipmentTemplate(t.subject, bulkItems, ctx, false));
          loadBody(fillMultipleShipmentTemplate(t.template, bulkItems, ctx, true));
          return;
        }
        const ctx = { trackingUrl, shipDate, locationName };
        setSubject(fillShipmentTemplate(t.subject, item, ctx, false));
        loadBody(fillShipmentTemplate(t.template, item, ctx, true));
      })
      .catch(() => {}); // no template / not reachable: nothing shown
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, locationId, locations, trackingUrl, shipDate]);

  // The sender for the location: the user's SMTP, as Send Approval shows it.
  useEffect(() => {
    setSmtpEmail(null);
    setSmtpChecked(false);
    if (!item || !locationId) return undefined;
    let live = true;
    API.post(
      "/user/smtp/check",
      { locationId },
      { headers: authHeader() },
    )
      .then(({ data }) => {
        if (!live) return;
        setSmtpEmail(data?.smtpEmail || null);
        setSmtpChecked(true);
      })
      .catch(() => live && setSmtpChecked(true));
    return () => {
      live = false;
    };
  }, [item, locationId]);

  const send = async () => {
    setError("");
    if (!emailTo.trim()) return setError("Enter an email address");
    if (!subject.trim()) return setError("Enter a subject");
    if (!locationId) return setError("Choose the brand to send from");
    try {
      setSending(true);
      const { data } = await API.post(
        "/ups-tracking/email",
        {
          ...(isBulk ? { ids: bulkItems.map((r) => r.id) } : { id: item.id }),
          emailTo,
          subject,
          // Single spaced in the mail client too (user, 2026-10-27).
          body: emailBodyHtml(body),
          locationId,
        },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Email failed");
      onSent?.(data.data, data.message);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSending(false);
    }
  };

  const emailedBefore = isBulk
    ? (() => {
        const n = bulkItems.filter((r) => r.emailedAt).length;
        return n ? `${n} of these ${bulkItems.length} were already emailed` : null;
      })()
    : item?.emailedAt
    ? `Already emailed to ${item.emailedTo || "-"} on ${new Date(
        item.emailedAt,
      ).toLocaleString()}`
    : null;

  return (
    <Modal size="lg" show={!!item} onHide={sending ? undefined : onHide}>
      <Modal.Body className="shadow-lg bg-white px-3" style={{ borderRadius: 15 }}>
        {sending && <Spinner />}
        <h4 className="mb-1">
          {isBulk ? `Email ${bulkItems.length} Shipments` : "Email Shipment"}
        </h4>
        <p className="text-muted small mb-2">
          {isBulk
            ? bulkItems.map((r) => r.trackingNumber).join(", ")
            : item?.trackingNumber}
          {item?.customer?.customerName ? ` · ${item.customer.customerName}` : ""}
        </p>
        <hr className="mb-2 mt-1" />

        {emailedBefore && (
          <div className="alert alert-info py-1 mb-2 small" role="status">
            <i className="mdi mdi-email-check-outline me-1"></i>
            {emailedBefore}
          </div>
        )}

        {!item?.locationId && (
          <div className="row align-items-center mb-2 g-2">
            <label
              htmlFor="shipment-email-location"
              className="col-auto form-label mb-0"
              style={{ width: LABEL_COL_WIDTH }}
            >
              Brand
            </label>
            <div className="col">
              <select
                id="shipment-email-location"
                className="form-select"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                <option value="">Send from which brand's email?</option>
                {locations.map((l) => (
                  <option key={l.id} value={String(l.id)}>
                    {l.locationName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="mb-1">
          <small className="text-muted">
            Separate multiple by comma
            {smtpEmail && <> &nbsp;·&nbsp; Email from : {smtpEmail}</>}
          </small>
        </div>
        <div className="row align-items-center mb-2 g-2">
          <label
            htmlFor="shipment-email-to"
            className="col-auto form-label mb-0"
            style={{ width: LABEL_COL_WIDTH }}
          >
            Email To
          </label>
          <div className="col">
            <input
              id="shipment-email-to"
              type="email"
              className="form-control"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
            />
          </div>
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={!contactCustomerId}
              title={
                contactCustomerId
                  ? "Add the customer's contacts"
                  : "No customer linked to this shipment"
              }
              aria-label="Customer contacts"
              onClick={() => setShowContacts(true)}
            >
              <i className="mdi mdi-card-account-mail-outline"></i>
            </button>
          </div>
        </div>

        {locationId && smtpChecked && !smtpEmail && (
          <div className="mb-2 text-danger small">
            Please Add SMTP Settings in user settings.
          </div>
        )}

        <div className="row align-items-center mb-2 g-2">
          <label
            htmlFor="shipment-email-subject"
            className="col-auto form-label mb-0"
            style={{ width: LABEL_COL_WIDTH }}
          >
            Subject
          </label>
          <div className="col">
            <input
              id="shipment-email-subject"
              type="text"
              className="form-control"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
        </div>

        <label className="form-label mb-1">Email Body</label>
        <EmailBodyEditor
          html={bodyLoad.html}
          loadKey={bodyLoad.key}
          onChange={setBody}
        />

        {error && (
          <div className="alert alert-danger py-1 my-2" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex justify-content-end gap-1 mt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onHide}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={send}
            disabled={sending || (smtpChecked && !smtpEmail)}
          >
            <i className="mdi mdi-send me-1"></i>
            {sending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </Modal.Body>

      <Modal size="xl" show={showContacts} onHide={() => setShowContacts(false)}>
        <Modal.Body className="p-0">
          {showContacts && (
            <CustomerContactSelect
              customerId={contactCustomerId}
              isMultiSelect
              handleSelectContact={addContacts}
            />
          )}
        </Modal.Body>
      </Modal>
    </Modal>
  );
}
